/**
 * TouchController — gesture-based touch input for Puyo-style gameplay.
 *
 * Gestures:
 *  - Horizontal pan (1 finger ONLY): move piece left/right
 *  - Two-finger rotation: rotate piece CW or CCW (NO pan while 2 fingers)
 *  - Swipe down (1 finger): hard drop
 *  - Hold + drag down (1 finger): soft drop
 *  - Tap (quick touch & release): hard drop
 */

export interface TouchActions {
  moveLeft():  void
  moveRight(): void
  rotateCW():  void
  rotateCCW(): void
  hardDrop():  void
  softDropStart(): void
  softDropEnd():   void
}

const PAN_THRESHOLD    = 36   // pixels of horizontal drag per move
const SWIPE_DOWN_DIST  = 80   // pixels down to trigger hard drop
const SWIPE_DOWN_SPEED = 400  // px/s velocity threshold for hard drop
const SOFT_DROP_DIST   = 40   // pixels down (slow) to start soft drop
const ROTATE_ANGLE     = 25   // degrees of two-finger rotation per action
const TAP_MAX_TIME     = 180  // ms: if touch < this and barely moved, it's a tap
const TAP_MAX_DIST     = 15   // px: max movement to still count as tap

export class TouchController {
  private scene: Phaser.Scene
  private actions: TouchActions
  private enabled = true

  // Pan tracking (1-finger only)
  private panStartX   = 0
  private panAccum    = 0

  // Vertical tracking
  private panStartY      = 0
  private softDropActive = false
  private hardDropFired  = false

  // Tap detection
  private touchStartTime = 0

  // Two-finger rotation
  private prevAngle: number | null = null

  // Track finger count to reset pan on transitions
  private wasTwoFinger = false

  constructor(scene: Phaser.Scene, actions: TouchActions) {
    this.scene   = scene
    this.actions = actions
    this.setup()
  }

  /** Call this when a new piece spawns to prevent leftover gestures carrying over. */
  resetGesture(): void {
    this.panAccum      = 0
    this.hardDropFired = false
    this.prevAngle     = null
    this.wasTwoFinger  = false
    if (this.softDropActive) {
      this.softDropActive = false
      this.actions.softDropEnd()
    }
    // Reset pan origin to current pointer position so no jump happens
    const p1 = this.scene.input.pointer1
    if (p1?.isDown) {
      this.panStartX = p1.x
      this.panStartY = p1.y
    }
  }

  setEnabled(v: boolean): void {
    this.enabled = v
    if (!v && this.softDropActive) {
      this.softDropActive = false
      this.actions.softDropEnd()
    }
  }

  destroy(): void {
    this.scene.input.off('pointerdown',  this.onPointerDown,  this)
    this.scene.input.off('pointermove',  this.onPointerMove,  this)
    this.scene.input.off('pointerup',    this.onPointerUp,    this)
  }

  private setup(): void {
    // Enable multi-touch
    this.scene.input.addPointer(1)  // allow 2 pointers total

    this.scene.input.on('pointerdown', this.onPointerDown, this)
    this.scene.input.on('pointermove', this.onPointerMove, this)
    this.scene.input.on('pointerup',   this.onPointerUp,   this)
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled) return

    const pointers = this.getActivePointers()

    // If second finger just arrived, switch to rotation mode — kill any pan state
    if (pointers.length === 2) {
      this.wasTwoFinger = true
      this.panAccum     = 0
      this.prevAngle    = this.angleBetween(pointers[0], pointers[1])
      // End soft drop if active (two fingers = rotation only)
      if (this.softDropActive) {
        this.softDropActive = false
        this.actions.softDropEnd()
      }
      return
    }

    // Single finger down — fresh gesture
    this.panStartX      = pointer.x
    this.panStartY      = pointer.y
    this.panAccum       = 0
    this.hardDropFired  = false
    this.touchStartTime = pointer.downTime
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled || !pointer.isDown) return

    const pointers = this.getActivePointers()

    // ── Two-finger rotation (NO pan) ──────────────────────────────────────
    if (pointers.length === 2) {
      this.wasTwoFinger = true
      const angle = this.angleBetween(pointers[0], pointers[1])
      if (this.prevAngle !== null) {
        let delta = angle - this.prevAngle
        // Normalize to [-180, 180]
        if (delta > 180)  delta -= 360
        if (delta < -180) delta += 360

        if (Math.abs(delta) >= ROTATE_ANGLE) {
          if (delta > 0) this.actions.rotateCW()
          else           this.actions.rotateCCW()
          this.prevAngle = angle
        }
      } else {
        this.prevAngle = angle
      }
      return  // BLOCK all pan/drop while 2 fingers are down
    }

    // ── Transition from 2→1 finger: reset pan origin ─────────────────────
    if (this.wasTwoFinger) {
      this.wasTwoFinger  = false
      this.prevAngle     = null
      this.panStartX     = pointer.x
      this.panStartY     = pointer.y
      this.panAccum      = 0
      this.hardDropFired = false
      return  // skip this frame to avoid jump
    }

    // ── Single-finger pan ────────────────────────────────────────────────
    const dx = pointer.x - this.panStartX
    const dy = pointer.y - this.panStartY

    // Horizontal movement
    const total = dx + this.panAccum
    const moves = Math.floor(Math.abs(total) / PAN_THRESHOLD)
    if (moves > 0) {
      const dir = total > 0 ? 1 : -1
      for (let i = 0; i < moves; i++) {
        if (dir > 0) this.actions.moveRight()
        else         this.actions.moveLeft()
      }
      this.panAccum  = total - dir * moves * PAN_THRESHOLD
      this.panStartX = pointer.x
    }

    // Vertical: check for hard drop (fast swipe down) or soft drop (slow drag)
    if (!this.hardDropFired && dy > SWIPE_DOWN_DIST) {
      const elapsed = (pointer.moveTime - pointer.downTime) / 1000
      const velocity = dy / Math.max(elapsed, 0.01)
      if (velocity > SWIPE_DOWN_SPEED) {
        this.actions.hardDrop()
        this.hardDropFired = true
        if (this.softDropActive) {
          this.softDropActive = false
          this.actions.softDropEnd()
        }
        return
      }
    }

    // Soft drop: dragging down slowly
    if (!this.hardDropFired && dy > SOFT_DROP_DIST && !this.softDropActive) {
      this.softDropActive = true
      this.actions.softDropStart()
    } else if (this.softDropActive && dy <= SOFT_DROP_DIST * 0.5) {
      this.softDropActive = false
      this.actions.softDropEnd()
    }
  }

  private onPointerUp(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled) return

    // End soft drop
    if (this.softDropActive) {
      this.softDropActive = false
      this.actions.softDropEnd()
    }

    const pointers = this.getActivePointers()

    // If we were in two-finger mode and one finger lifted, reset for clean 1-finger use
    if (this.wasTwoFinger) {
      this.prevAngle = null
      if (pointers.length === 1) {
        // Reset pan origin to remaining finger
        this.panStartX = pointers[0].x
        this.panStartY = pointers[0].y
        this.panAccum  = 0
      }
      if (pointers.length < 2) {
        this.wasTwoFinger = false
      }
      return  // don't fire tap after rotation
    }

    // Tap detection: quick touch with minimal movement → hard drop
    const dt   = pointer.upTime - this.touchStartTime
    const dist = Phaser.Math.Distance.Between(
      this.panStartX, this.panStartY, pointer.upX, pointer.upY,
    )
    if (dt < TAP_MAX_TIME && dist < TAP_MAX_DIST && !this.hardDropFired) {
      this.actions.hardDrop()
    }

    // Reset rotation
    if (pointers.length < 2) this.prevAngle = null
  }

  private getActivePointers(): Phaser.Input.Pointer[] {
    const ptrs: Phaser.Input.Pointer[] = []
    if (this.scene.input.pointer1?.isDown) ptrs.push(this.scene.input.pointer1)
    if (this.scene.input.pointer2?.isDown) ptrs.push(this.scene.input.pointer2)
    return ptrs
  }

  private angleBetween(a: Phaser.Input.Pointer, b: Phaser.Input.Pointer): number {
    return Phaser.Math.RadToDeg(Math.atan2(b.y - a.y, b.x - a.x))
  }
}

/** Returns true if the device likely supports touch. */
export function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}
